/**
 * Sticky add-to-cart bar for the show PDP.
 *
 * Reveals a fixed buy bar once the in-page button has scrolled out of view, and
 * hides it again when that button is back on screen — so there is never a
 * duplicate CTA competing with one the customer can already see.
 *
 * The bar's own button lives outside the form and reaches it via the `form`
 * attribute, so this file only has to handle visibility and keeping the label
 * in step with the chosen category.
 */

(function () {
  "use strict";

  var bar = document.querySelector("[data-sticky-cta]");
  if (!bar) return;

  /* The FIRST in-page button is the anchor. It sits directly under the category
     picker, so the bar appears exactly when the customer scrolls past the
     decision rather than at the bottom of the page. */
  var anchor = document.querySelector(".pdp__cta");
  if (!anchor) return;

  var nameEl = bar.querySelector("[data-sticky-name]");
  var priceEl = bar.querySelector("[data-sticky-price]");

  /* Publish the bar's real height so the floating basket can step over it
     instead of landing on the ADD TO CART button. Measured rather than assumed,
     because the bar's height follows its font and padding. offsetHeight is read
     while the bar is laid out (it is only ever transformed and faded, never
     display:none), so this is valid before the first reveal. */
  function publishHeight() {
    var h = bar.offsetHeight;
    if (h) document.documentElement.style.setProperty("--sticky-cta-h", h + "px");
  }

  function show(on) {
    bar.classList.toggle("is-visible", on);
    /* Drives .cart-fab's lift in theme.css. On the body rather than a sibling
       selector because the bar lives inside the section and the basket is
       rendered from the layout, so the two are not siblings. */
    document.body.classList.toggle("has-sticky-cta", on);
    /* Hidden from assistive tech too while off screen, so the same action is
       not announced twice. */
    bar.setAttribute("aria-hidden", on ? "false" : "true");
  }

  publishHeight();
  if ("ResizeObserver" in window) new ResizeObserver(publishHeight).observe(bar);
  else window.addEventListener("resize", publishHeight);

  /* Deliberately a scroll listener rather than an IntersectionObserver.

     An observer only fires when the intersection ratio CROSSES a threshold. A
     jump from below the anchor to above it in a single frame goes ratio 0 ->
     ratio 0, so no callback runs and the bar stays hidden for the rest of the
     page. That is not hypothetical: it happens on an in-page anchor link, on
     browser scroll restoration when returning via Back, and on any scripted
     scrollTo. A thumb scroll passes through the intersecting state and hides
     the hole, which is what made it survive this long.

     This recomputes from the anchor's position instead of from a transition, so
     it is correct wherever the scroll position came from. The read is throttled
     to one per animation frame, which is the same rate a smoothly-scrolling
     observer would have delivered callbacks at anyway. */
  var queued = false;

  function evaluate() {
    queued = false;
    /* Fully above the top edge — the same condition the observer expressed as
       "not intersecting AND top < 0", just measured directly. Scrolling down
       past the bar's own position must not trigger it, and neither should the
       button not having been reached yet. */
    show(anchor.getBoundingClientRect().bottom <= 0);
  }

  function request() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(evaluate);
  }

  window.addEventListener("scroll", request, { passive: true });
  window.addEventListener("resize", request);
  /* Covers the load case: a restored scroll position or a #fragment target puts
     the page mid-document before a single scroll event has fired. */
  window.addEventListener("pageshow", request);
  evaluate();

  /* Keep the bar's label and price in step with the category radios, so what it
     offers is always what is actually selected. */
  var radios = [].slice.call(document.querySelectorAll('.category input[type="radio"]'));

  function sync() {
    var picked = radios.filter(function (r) { return r.checked; })[0];
    if (!picked) return;

    var option = picked.closest(".category__option");
    var label = option ? option.querySelector(".category__name") : null;

    if (nameEl && label) nameEl.textContent = label.textContent.trim();
    /* data-price is already a formatted money string from Liquid, so nothing
       here has to guess at currency formatting. */
    if (priceEl && picked.dataset.price) priceEl.textContent = picked.dataset.price;
  }

  radios.forEach(function (r) { r.addEventListener("change", sync); });
  sync();
})();
